import { useEffect, useRef, useState } from 'react';
import { TICKER_CHART_EVENT } from '../tickerChart';
import { OrderTicket } from './OrderTicket';

/**
 * Full-screen TradingView chart modal. Mounted once at the App root; opens when
 * any component dispatches the TICKER_CHART_EVENT with a ticker symbol.
 *
 * Uses TradingView's Advanced Chart embed (external script from tradingview.com).
 * The app's Express server disables CSP, so the external script loads fine in the
 * browser. Nothing here runs on prod's read-only path — it's a client-only widget.
 */
export function TickerChart() {
  const [ticker, setTicker] = useState<string | null>(null);
  const [armed, setArmed] = useState(false); // order buttons only when local execution is enabled
  const [buying, setBuying] = useState(false);
  const [held, setHeld] = useState<{ position: number; avgCost: number; mark: number | null } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/api/orders/armed')
      .then((r) => (r.ok ? r.json() : { armed: false }))
      .then((d) => setArmed(Boolean(d.armed)))
      .catch(() => setArmed(false));
  }, []);

  // Listen for global open requests
  useEffect(() => {
    const onOpen = (e: Event) => {
      const t = (e as CustomEvent<string>).detail;
      if (t) setTicker(t.toUpperCase());
    };
    window.addEventListener(TICKER_CHART_EVENT, onOpen);
    return () => window.removeEventListener(TICKER_CHART_EVENT, onOpen);
  }, []);

  // If the open ticker is one of your IBKR positions, surface your entry price
  useEffect(() => {
    if (!ticker) return;
    setHeld(null);
    fetch('/api/positions')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.connected) return;
        const p = (d.positions || []).find(
          (x: any) => x.ticker === ticker && x.secType === 'STK'
        );
        if (p) setHeld({ position: p.position, avgCost: p.avgCost, mark: p.mark ?? null });
      })
      .catch(() => {
        /* not configured (prod) or gateway down — header just omits the entry info */
      });
  }, [ticker]);

  // Close on Escape
  useEffect(() => {
    if (!ticker) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTicker(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ticker]);

  // (Re)build the TradingView widget whenever the ticker changes
  useEffect(() => {
    const container = containerRef.current;
    if (!ticker || !container) return;

    container.innerHTML = '';
    const script = document.createElement('script');
    script.src =
      'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: ticker,
      interval: 'D',
      timezone: 'Etc/UTC',
      theme: 'dark',
      style: '1',
      locale: 'en',
      hide_side_toolbar: false,
      allow_symbol_change: true,
      calendar: false,
      support_host: 'https://www.tradingview.com',
    });
    container.appendChild(script);

    return () => {
      container.innerHTML = '';
    };
  }, [ticker]);

  if (!ticker) return null;

  return (
    <div
      onClick={() => setTicker(null)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 10, 2, 0.85)',
        backdropFilter: 'blur(3px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2vh 2vw',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '96vw',
          height: '92vh',
          maxWidth: '1500px',
          background: '#0a0f0a',
          border: '1px solid #00ff41',
          borderRadius: '8px',
          boxShadow: '0 0 40px rgba(0, 255, 65, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            borderBottom: '1px solid rgba(0, 255, 65, 0.25)',
            fontFamily: "'SF Mono', 'Menlo', monospace",
          }}
        >
          <span style={{ color: '#00ff41', fontWeight: 700, letterSpacing: '0.05em' }}>
            📈 {ticker}
            {held && (
              <span
                style={{
                  marginLeft: '14px',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#569a68',
                }}
              >
                you hold {held.position} @ ${held.avgCost.toFixed(2)}
                {held.mark !== null && held.avgCost > 0 && (
                  <span
                    style={{
                      marginLeft: '8px',
                      color: held.mark >= held.avgCost ? '#00ff41' : '#ff3b3b',
                    }}
                  >
                    ({held.mark >= held.avgCost ? '+' : ''}
                    {(((held.mark - held.avgCost) / held.avgCost) * 100).toFixed(1)}% since entry)
                  </span>
                )}
              </span>
            )}
          </span>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {armed && (
              <button
                className="buy-btn"
                title={`Buy ${ticker}`}
                onClick={() => setBuying(true)}
                style={{ fontSize: '12px', padding: '5px 14px' }}
              >
                BUY
              </button>
            )}
            <button
              onClick={() => setTicker(null)}
              style={{
                background: 'transparent',
                border: '1px solid rgba(0, 255, 65, 0.4)',
                color: '#00ff41',
                borderRadius: '4px',
                padding: '4px 12px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              ✕ Close (Esc)
            </button>
          </div>
        </div>
        <div className="tradingview-widget-container" ref={containerRef} style={{ flex: 1 }} />
      </div>

      {buying && (
        <div onClick={(e) => e.stopPropagation()}>
          <OrderTicket
            ticker={ticker}
            action="BUY"
            maxQty={1}
            mark={held?.mark ?? null}
            onClose={() => setBuying(false)}
          />
        </div>
      )}
    </div>
  );
}
